import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// Create axios instance with base configuration
const axiosInstance: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1',
  timeout: 15000,
  // H6: send the httpOnly auth cookie with every request (required cross-origin in
  // dev; harmless same-origin in prod). The cookie now carries auth instead of a
  // localStorage token, so the token can't be exfiltrated by XSS.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token to headers
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from localStorage
    const token = localStorage.getItem('accessToken');

    if (token && config.headers) {
      // Add Authorization header with Bearer token
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors globally
axiosInstance.interceptors.response.use(
  (response) => {
    // Return successful response
    return response;
  },
  (error: AxiosError) => {
    // Handle 401 Unauthorized errors (token expiration)
    if (error.response?.status === 401) {
      // Clear authentication data
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');

      // Redirect to login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    // Handle 403 Forbidden errors
    if (error.response?.status === 403) {
      console.error('Access forbidden - insufficient permissions');
    }

    // Handle 404 Not Found errors
    if (error.response?.status === 404) {
      console.error('Resource not found');
    }

    // Handle 500 Internal Server Error
    if (error.response?.status === 500) {
      console.error('Server error - please try again later');
    }

    return Promise.reject(error);
  }
);

// Helper function to extract error message from API response
export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    // Check if response contains error message
    if (error.response?.data?.message) {
      return error.response.data.message;
    }

    // Check if response contains error field (alternative format)
    if (error.response?.data?.error) {
      return error.response.data.error;
    }

    // Check if response contains error array
    if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
      return error.response.data.errors.join(', ');
    }

    // Use error message (not status text)
    if (error.message) {
      return error.message;
    }

    // Use status text as last resort
    if (error.response?.statusText) {
      return error.response.statusText;
    }
  }

  // Default error message
  return 'An unexpected error occurred';
};

export default axiosInstance;
